/**
 * The view onto the bench: zoom, pan, the grid, and snapping
 * (optics-bench-target.md §The view, §The grid).
 *
 * All of it is SURFACE state. None of it is in the engine's World and none of
 * it changes a number — snapping only decides which round number a drag
 * writes into the world.
 */
import type { optics as O } from '@padcanvas/shared';

type Vec2 = O.Vec2;

/** What is on screen: the bench point at the centre, and how many cm across. */
export interface View { cx: number; cy: number; width: number }

/** The widest and narrowest the view goes: 5 cm (4000%) to 40 m (5%, twenty
 *  times the 2 m default). */
export const VIEW_MIN = 5;
export const VIEW_MAX = 4000;

/**
 * 100% zoom: 2 m of bench across the view — the longest school optical bench
 * with room to spare, so every Class 10 arrangement fits at the default scale.
 * Zoom is a multiple of this scale: 200% shows 1 m, 50% shows 4 m.
 */
export const DEFAULT_WIDTH = 200;

/** Where the bench opens: the rail's zero mark at the centre, at 100%. */
export const OPENING_VIEW: View = { cx: 0, cy: 0, width: DEFAULT_WIDTH };

/** The zoom levels marked on the slider; clicking one goes there. 100% is the reset. */
export const ZOOM_MARKS = [400, 200, 100, 50, 25] as const;

/** The view width for a zoom percentage: 100% is 2 m, 200% is 1 m. */
export const widthForPercent = (pct: number): number => (DEFAULT_WIDTH * 100) / pct;

/** Where a zoom level sits along the slider, 0 at its left end to 1 at its right. */
export const markFraction = (pct: number): number => widthToSlider(widthForPercent(pct)) / SLIDER_MAX;

/** The marked level nearest a point on the track (0–1 along it). */
export function nearestMark(fraction: number): number {
    return ZOOM_MARKS.reduce((best, pct) =>
        (Math.abs(markFraction(pct) - fraction) < Math.abs(markFraction(best) - fraction) ? pct : best), ZOOM_MARKS[0] as number);
}

/** How close, in px of track, a dragged thumb must come to a mark to snap onto it. */
export const MARK_SNAP_PX = 10;

/** The zoom, as a percentage of the default scale. */
export const zoomPercent = (width: number): number => (DEFAULT_WIDTH / width) * 100;

/** "100%", "250%", "12.5%": whole numbers once they are big enough to read that way. */
export function zoomLabel(width: number): string {
    const pct = zoomPercent(width);
    return `${pct >= 10 ? Math.round(pct) : +pct.toFixed(1)}%`;
}

// ── the zoom slider ─────────────────────────────────────────────────────────

/** The slider runs 0–1000, logarithmic: each step changes the scale by one ratio. */
export const SLIDER_MAX = 1000;
export const sliderToWidth = (s: number): number =>
    VIEW_MIN * Math.pow(VIEW_MAX / VIEW_MIN, Math.min(SLIDER_MAX, Math.max(0, s)) / SLIDER_MAX);
export const widthToSlider = (w: number): number =>
    (SLIDER_MAX * Math.log(clampWidth(w) / VIEW_MIN)) / Math.log(VIEW_MAX / VIEW_MIN);
export const clampWidth = (w: number): number => Math.min(VIEW_MAX, Math.max(VIEW_MIN, w));

/** "60 cm", "2.5 m" — the view width in the unit a student thinks in. */
export function widthLabel(w: number): string {
    if (w >= 100) {
        const m = w / 100;
        return `${m >= 10 ? m.toFixed(0) : m.toFixed(1).replace(/\.0$/, '')} m`;
    }
    return `${w >= 10 ? w.toFixed(0) : w.toFixed(1).replace(/\.0$/, '')} cm`;
}

/** Zoom to `width`, keeping the bench point under `anchor` where it is on screen. */
export function zoomAbout(v: View, width: number, anchor: Vec2): View {
    const w = clampWidth(width);
    const k = w / v.width;
    return { width: w, cx: anchor.x + (v.cx - anchor.x) * k, cy: anchor.y + (v.cy - anchor.y) * k };
}

/** How much of the view Fit gives to the content: 80%, leaving 10% clear on each side. */
export const FIT_SHARE = 0.8;

/**
 * The view that puts `points` in the middle 80% of the panel, across and up,
 * in a panel of this aspect (height / width). With nothing to show, the
 * opening view.
 */
export function fitView(points: Vec2[], aspect: number): View {
    if (points.length === 0) return OPENING_VIEW;
    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const byWidth = (x1 - x0) / FIT_SHARE;
    const byHeight = (y1 - y0) / FIT_SHARE / Math.max(aspect, 1e-6);
    return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, width: clampWidth(Math.max(byWidth, byHeight, 20)) };
}

// ── ticks and grid ──────────────────────────────────────────────────────────

/** The smallest 1, 2 or 5 × 10ⁿ that is at least `min`. */
export function niceStep(min: number): number {
    const p = Math.pow(10, Math.floor(Math.log10(min)));
    for (const m of [1, 2, 5, 10]) if (m * p >= min - 1e-12) return m * p;
    return 10 * p;
}

export interface Grid {
    /** cm between minor lines, and how visible they are (fading out as they crowd). */
    minor: number;
    minorOpacity: number;
    major: number;
}

/** Minor lines 12–30 px apart; majors every 5 or 10 minors. */
export function gridFor(pxPerCm: number): Grid {
    const minor = niceStep(12 / pxPerCm);
    const px = minor * pxPerCm;
    const lead = Math.round(minor / Math.pow(10, Math.floor(Math.log10(minor))));
    // Every fifth line for a 2-step (2 → 10), every tenth otherwise (1 → 10, 5 → 50).
    const major = minor * (lead === 2 ? 5 : 10);
    // Fade out as the lines approach the 12 px floor, so a step change is a fade.
    const minorOpacity = Math.min(1, Math.max(0, (px - 12) / 12));
    return { minor, minorOpacity, major };
}

/**
 * Ticks along the rail: 5–12 across the view, labelled cm up to a metre, m
 * beyond. Only where there is rail, when `rail` is given.
 */
export function railTicks(view: View, rail?: { from: number; to: number }): { at: number; label: string }[] {
    const step = niceStep(view.width / 12);
    const left = Math.max(rail?.from ?? -Infinity, view.cx - view.width / 2);
    const from = Math.ceil(left / step) * step;
    const to = Math.min(rail?.to ?? Infinity, view.cx + view.width / 2);
    const out: { at: number; label: string }[] = [];
    for (let x = from; x <= to + 1e-9 && out.length < 40; x += step) {
        const r = Math.round(x / step) * step;
        const text = Math.abs(r) >= 100 && step >= 50 ? `${+(r / 100).toFixed(2)} m` : `${+r.toFixed(2)}`;
        // A true minus sign, as in the working lines: "−40", not "-40".
        out.push({ at: r, label: text.replace('-', '−') });
    }
    return out;
}

// ── snapping ────────────────────────────────────────────────────────────────

/** A point a drag may snap to, with what to call it. */
export interface SnapTarget { at: Vec2; label: string }

export interface SnapResult {
    at: Vec2;
    /** What it snapped to, for the guide and the live readout. */
    to?: string;
    /** Dashed alignment guides to draw while the snap holds. */
    guides: { from: Vec2; to: Vec2 }[];
}

/** Snap radius on screen, px. */
export const SNAP_PX = 8;

/**
 * Where a drag lands, in the target's priority order: named points, the axis
 * (free parts), alignment with other parts, then the minor grid.
 * `onAxis` parts move only along x.
 */
export function snapDrag(
    raw: Vec2,
    opts: { onAxis: boolean; pxPerCm: number; named: SnapTarget[]; others: Vec2[]; enabled: boolean },
): SnapResult {
    const r = SNAP_PX / opts.pxPerCm;
    const base: Vec2 = opts.onAxis ? { x: raw.x, y: 0 } : raw;
    if (!opts.enabled) return { at: base, guides: [] };

    for (const t of opts.named) {
        const d = opts.onAxis ? Math.abs(t.at.x - base.x) : Math.hypot(t.at.x - base.x, t.at.y - base.y);
        if (d <= r) return { at: opts.onAxis ? { x: t.at.x, y: 0 } : t.at, to: t.label, guides: [] };
    }

    const step = gridFor(opts.pxPerCm).minor;
    const round = (x: number): number => Math.round(x / step) * step;
    let x = round(base.x);
    let y = opts.onAxis ? 0 : round(base.y);
    const guides: SnapResult['guides'] = [];
    let to: string | undefined;

    if (!opts.onAxis) {
        if (Math.abs(base.y) <= r) { y = 0; to = 'the axis'; }
        const alignX = opts.others.find(o => Math.abs(o.x - base.x) <= r);
        const alignY = opts.others.find(o => Math.abs(o.y - base.y) <= r);
        if (alignX) { x = alignX.x; guides.push({ from: alignX, to: { x, y } }); }
        if (alignY && to === undefined) { y = alignY.y; guides.push({ from: alignY, to: { x, y } }); }
    }
    return { at: { x, y }, to, guides };
}

/** Angles snap to 5°, or 1° when zoomed in close (more than 20 px to the cm). */
export function snapAngle(rad: number, pxPerCm: number, enabled: boolean): number {
    if (!enabled) return rad;
    const step = ((pxPerCm > 20 ? 1 : 5) * Math.PI) / 180;
    return Math.round(rad / step) * step;
}

// ── the ray animation ────────────────────────────────────────────────────────

/** Light crosses one full view width in this long, whatever the zoom. */
export const RAY_SWEEP_MS = 3000;

/** A path cut off `s` along its length, and the direction its tip is heading. */
export function truncatePath(path: Vec2[], s: number): { points: Vec2[]; tipDir: Vec2 | null; done: boolean } {
    const out: Vec2[] = [path[0]];
    let left = s;
    for (let i = 1; i < path.length; i++) {
        const a = path[i - 1], b = path[i];
        const L = Math.hypot(b.x - a.x, b.y - a.y);
        const dir = L > 0 ? { x: (b.x - a.x) / L, y: (b.y - a.y) / L } : { x: 1, y: 0 };
        if (left <= L) {
            out.push({ x: a.x + dir.x * left, y: a.y + dir.y * left });
            return { points: out, tipDir: dir, done: false };
        }
        left -= L;
        out.push(b);
    }
    return { points: out, tipDir: null, done: true };
}

/**
 * How far along a path the light must travel before the rest of it is out of
 * sight: to its last point inside the view, and on to the view's edge. The
 * animation ends when every ray has got that far.
 */
export function visibleLength(path: Vec2[], view: View, aspect: number): number {
    const h = view.width * aspect;
    const inside = (p: Vec2): boolean =>
        Math.abs(p.x - view.cx) <= view.width / 2 && Math.abs(p.y - view.cy) <= h / 2;
    const diag = Math.hypot(view.width, h);
    let run = 0, reach = 0;
    for (let i = 1; i < path.length; i++) {
        const L = Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
        if (inside(path[i - 1]) || inside(path[i])) reach = run + Math.min(L, diag);
        run += L;
    }
    return reach;
}
